import type {
  AuthError as SupabaseAuthError,
  Session,
  SupabaseClient,
  User,
} from "@/lib/supabase";
import { SignalError } from "@mfg/result";
import type { AuthUser } from "./types";
import {
  AuthClientError,
  AuthNetworkError,
  AuthOperationFailed,
  AuthServerError,
  AuthUnexpectedError,
  AuthUpdateFailed,
  InvalidCredentials,
  NoSession,
  OtpFailed,
  PasswordError,
  TokenExpired,
  UserAlreadyExists,
} from "./_errors";

/**
 * Minimal client shape this package depends on. Both browser and server SSR
 * Supabase clients satisfy it. Accepting the structural shape keeps the
 * package independent of which client factory the caller used.
 */
export type AnyAuthClient = Pick<SupabaseClient, "auth">;

type AuthOps = AnyAuthClient["auth"];
type VerifyOtpParams = Parameters<AuthOps["verifyOtp"]>[0];
type UpdateUserAttrs = Parameters<AuthOps["updateUser"]>[0];
type SetSessionParams = Parameters<AuthOps["setSession"]>[0];
type AuthStateCallback = Parameters<AuthOps["onAuthStateChange"]>[0];
type ResetPasswordOptions = Parameters<AuthOps["resetPasswordForEmail"]>[1];

function toAuthUser(user: User): AuthUser {
  return {
    id: user.id,
    email: user.email,
    created_at: user.created_at,
    user_metadata: user.user_metadata,
  };
}

/**
 * Map an unexpected (non-Signal) thrown error to AuthUnexpectedError, while
 * letting our own typed SignalErrors propagate untouched. Used in every
 * catch below so a NoSession/InvalidCredentials etc. is never re-wrapped.
 */
function asAuthError(e: unknown): never {
  if (e instanceof SignalError) throw e;
  throw new AuthUnexpectedError(
    e instanceof Error ? e.message : "Unknown error",
    {
      cause: e instanceof Error ? e : undefined,
    },
  );
}

export async function getCurrentUser(client: AnyAuthClient): Promise<AuthUser> {
  try {
    const { data, error } = await client.auth.getUser();
    if (error || !data?.user) {
      throw new NoSession(
        "getCurrentUser",
        error ? { cause: error } : undefined,
      );
    }
    return toAuthUser(data.user);
  } catch (e) {
    asAuthError(e);
  }
}

/**
 * Returns the authenticated user, or `null` when there is no active session.
 *
 * This is the single canonical NoSession→null narrowing point. All per-app
 * `getCurrentUserServer` wrappers delegate here so the narrowing logic lives
 * in exactly one place. Real failures (non-NoSession SignalErrors) propagate
 * unchanged — only the "not logged in" case is mapped to null.
 */
export async function getCurrentUserOrNull(
  client: AnyAuthClient,
): Promise<AuthUser | null> {
  try {
    return await getCurrentUser(client);
  } catch (e) {
    if (e instanceof NoSession) return null;
    throw e;
  }
}

export async function getSession(
  client: AnyAuthClient,
): Promise<Session | null> {
  try {
    const { data, error } = await client.auth.getSession();
    if (error) throw new AuthOperationFailed(error.message, { cause: error });
    return data.session;
  } catch (e) {
    asAuthError(e);
  }
}

export async function signInWithPassword(
  client: AnyAuthClient,
  email: string,
  password: string,
): Promise<void> {
  try {
    const { data: sessionData } = await client.auth.getSession();
    if (sessionData?.session) {
      const { error: refreshError } = await client.auth.refreshSession();
      if (!refreshError) return;
    }

    const { error } = await client.auth.signInWithPassword({ email, password });
    if (error) throwSignInError(error.message);
  } catch (e) {
    asAuthError(e);
  }
}

export async function signUp(
  client: AnyAuthClient,
  email: string,
  password: string,
  options?: { emailRedirectTo?: string; data?: Record<string, string> },
): Promise<{ needsEmailConfirmation: boolean }> {
  try {
    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: options?.emailRedirectTo,
        data: options?.data,
      },
    });
    if (error) throwSignUpError(error.message);
    // No session on signup = the project requires email confirmation first.
    return { needsEmailConfirmation: !data?.session };
  } catch (e) {
    asAuthError(e);
  }
}

/**
 * PKCE completion: exchange the ?code= from a Supabase email link (signup
 * confirmation, magic link, recovery) for a session. Browser-only by nature —
 * the code verifier lives in browser storage.
 */
export async function exchangeCodeForSession(
  client: AnyAuthClient,
  code: string,
): Promise<void> {
  try {
    const { error } = await client.auth.exchangeCodeForSession(code);
    if (error) throw new AuthClientError(error.message);
  } catch (e) {
    asAuthError(e);
  }
}

export async function signOut(client: AnyAuthClient): Promise<void> {
  try {
    const { error } = await client.auth.signOut();
    if (error) throw new AuthOperationFailed(error.message, { cause: error });
  } catch (e) {
    asAuthError(e);
  }
}

export async function refreshSession(client: AnyAuthClient): Promise<AuthUser> {
  try {
    const { data, error } = await client.auth.refreshSession();
    if (error) throw classifyAuthError(error);
    if (!data?.user) throw new NoSession("refreshSession");
    return toAuthUser(data.user);
  } catch (e) {
    asAuthError(e);
  }
}

/**
 * Send a password-reset email. `options.redirectTo` controls the URL the
 * recipient lands on after clicking the link — typically your /auth/confirm
 * handler with `type=recovery` and a `next=` target.
 */
export async function resetPasswordForEmail(
  client: AnyAuthClient,
  email: string,
  options?: ResetPasswordOptions,
): Promise<void> {
  try {
    const { error } = await client.auth.resetPasswordForEmail(email, options);
    if (error) throw new AuthOperationFailed(error.message, { cause: error });
  } catch (e) {
    asAuthError(e);
  }
}

export async function verifyOtp(
  client: AnyAuthClient,
  params: VerifyOtpParams,
): Promise<AuthUser> {
  try {
    const { data, error } = await client.auth.verifyOtp(params);
    if (error || !data?.user) {
      throw new OtpFailed(
        error?.message ?? "OTP verification failed",
        error ? { cause: error } : undefined,
      );
    }
    return toAuthUser(data.user);
  } catch (e) {
    asAuthError(e);
  }
}

export async function updateUser(
  client: AnyAuthClient,
  attributes: UpdateUserAttrs,
): Promise<AuthUser> {
  try {
    const { data, error } = await client.auth.updateUser(attributes);
    if (error || !data?.user) {
      throw new AuthUpdateFailed(
        error?.message ?? "Update failed",
        error ? { cause: error } : undefined,
      );
    }
    return toAuthUser(data.user);
  } catch (e) {
    asAuthError(e);
  }
}

export async function setSession(
  client: AnyAuthClient,
  params: SetSessionParams,
): Promise<AuthUser> {
  try {
    const { data, error } = await client.auth.setSession(params);
    if (error || !data?.user) {
      throw new AuthOperationFailed(
        error?.message ?? "Set session failed",
        error ? { cause: error } : undefined,
      );
    }
    return toAuthUser(data.user);
  } catch (e) {
    asAuthError(e);
  }
}

export function onAuthStateChange(
  client: AnyAuthClient,
  callback: AuthStateCallback,
): ReturnType<AuthOps["onAuthStateChange"]> {
  return client.auth.onAuthStateChange(callback);
}

/**
 * Classify a Supabase AuthError into a typed signal error instance.
 * Used for paths where multiple failure modes share an entry point (e.g.
 * `refreshSession` can fail from network/server/expired-token). Pure
 * message/status sniffing — Supabase doesn't expose a stable typed enum.
 */
function classifyAuthError(
  error: SupabaseAuthError,
): AuthNetworkError | TokenExpired | AuthServerError | AuthOperationFailed {
  const msg = error.message.toLowerCase();
  if (
    msg.includes("network") ||
    msg.includes("fetch") ||
    msg.includes("offline")
  ) {
    return new AuthNetworkError(error.message, { cause: error });
  }
  if (
    msg.includes("expired") ||
    msg.includes("invalid") ||
    msg.includes("jwt")
  ) {
    return new TokenExpired(error.message, { cause: error });
  }
  const status = (error as SupabaseAuthError & { status?: number }).status;
  if (typeof status === "number" && status >= 500) {
    return new AuthServerError(error.message, { cause: error });
  }
  return new AuthOperationFailed(error.message, { cause: error });
}

function throwSignInError(message: string): never {
  if (message.includes("Invalid login credentials"))
    throw new InvalidCredentials();
  if (message.includes("expired")) throw new TokenExpired(message);
  if (message.includes("network")) throw new AuthNetworkError(message);
  throw new AuthOperationFailed(message);
}

function throwSignUpError(message: string): never {
  if (message.includes("already registered")) throw new UserAlreadyExists();
  if (message.includes("password")) throw new PasswordError(message);
  if (message.includes("network")) throw new AuthNetworkError(message);
  throw new AuthOperationFailed(message);
}
